import React, { FC, Fragment, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'react-use';
import ReactConfetti from 'react-confetti';
import { noop } from 'lodash';
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import { Check, Close, Remove, Star } from '@mui/icons-material';

import { useRouter } from '@cvt/routing';
import { PermissionedView } from '@cvt/components/Permissioned';
import { ListLoading } from '@cvt/components/layout/ListLoading';
import { FeedbackContext } from '@cvt/contexts';

import { Authenticated } from '@modules/Auth/components';
import { useMe } from '@modules/Auth/hooks/useMe';
import { MajorsList } from '@modules/Majors/partials/MajorsList';
import { MajorRow, MajorRowSkeleton } from '@modules/Majors/components/MajorRow';
import { DisciplinesList } from '@modules/Disciplines/partials/DisciplinesList';
import { DisciplineRow, DisciplineRowSkeleton } from '@modules/Disciplines/components/DisciplineRow';
import { UniversitiesList } from '@modules/Universities/partials/UniversitiesList';
import { UniversityRow, UniversityRowSkeleton } from '@modules/Universities/components/UniversityRow';
import { AcademicArchetype } from '@modules/Users/components/AcademicArchetype';
import { ArchetypeShareButton } from '@shared/components/ArchetypeShareButton';
import { LayoutContext } from '@shared/contexts/LayoutContext';
import { UserProgress } from '@shared/components/UserProgress';
import { AnimatedDots } from '@shared/components/AnimatedDots';
import { StartPageSwipableInfomration } from '../components/StartPageSwipableInfomration';

import { useAssertionCrud } from '../hooks/useAssertionCrud';
import { useAssertionBgImgs } from '../hooks/useAssertions';
import { SwipableCardStack } from '../components/SwipableCardStack';
import { SwipableCard } from '../components/SwipableCard';
import { AssertionCard } from '../components/AssertionCard';
import { AssertionCardLoading, FunFactLoading } from '../components/AssertionCardLoading';
import baseimage from '../components/assertion_base.webp';
import { useNextAssertionQuery } from '../hooks/useNextAssertionQuery';


interface Props {}

export const SwipableAssertions: FC<Props> = () => {
  const { isDesktop, isTablet } = useContext(LayoutContext);
  const { triggerFeedback } = React.useContext(FeedbackContext);
  const router = useRouter();

  const [isRevising, setIsRevising] = useState(false);

  const { answerAssertion, previousAssertion, calculateMatch } = useAssertionCrud();
  const { status, isFetching: isFetchingNext, next } = useNextAssertionQuery({ enabled: !isRevising });

  const isAtStageStart = useMemo(() => {
    if (!next) return false;
    return next.answered === 0;
  }, [next]);

  const [assertion, setAssertion] = useState<Assertions.Assertion>();

  const headerAndFooterHeight = useMemo(() => {
    if (isDesktop) {
      return 64;
    }
    if (isTablet) {
      return 120;
    }
    return 112;
  }, [isDesktop, isTablet]);

  const inBetweenStage = useMemo(() => {
    if (isRevising) return false;
    if (next) {
      const isStart = next.answered === 0;
      const isEnd = next.answered === next.assertionsPerStage;

      if (isStart) {
        return next.stage;
      }
      if (isEnd) {
        return 'end';
      }
    }

    return false;
  }, [next, isRevising]);

  const [isOngoing, setIsOngoing] = useState(!Boolean(inBetweenStage));

  const onContinue = useCallback(() => {
    switch (inBetweenStage) {
    case 'disciplines':
    case 'majors':
    case 'universities':
      setIsOngoing(true);
      break;
    case 'end':
      router.universities.go();
      break;
    }
  }, [inBetweenStage, router]);

  const isBackLoading = previousAssertion.isPending;
  const isCalculatingMatches = calculateMatch.isPending;

  const onAnswer = useCallback(
    async (answer: Assertions.AnswerValue) => {
      if (!assertion || isBackLoading) return;
      try {
        const response = await answerAssertion.mutateAsync({ id: assertion.id, answer });
        if (response.data.totalAnswered % 10 === 0) {
          try {
            await calculateMatch.mutateAsync();
          }
          catch (err: any) {
            if (err?.response?.status === 409) {
              triggerFeedback({
                severity: 'success',
                message: err.message,
              });
            }
          }}
      } finally {
        setIsRevising(false);
      }
    },
    [answerAssertion, assertion, calculateMatch, isBackLoading, triggerFeedback],
  );

  const isLoading = useMemo(() => {
    return answerAssertion.isPending || previousAssertion.isPending || isFetchingNext;
  }, [answerAssertion.isPending, previousAssertion.isPending, isFetchingNext]);

  const onBack = useCallback(async () => {
    try {
      const response = await previousAssertion.mutateAsync();
      const prev = response?.data?.previousAssertion;
      if (prev) {
        setAssertion(prev);
        setIsRevising(true);
        setIsOngoing(true);
      }
    } catch (e) {
      console.error(e);
    }
  }, [previousAssertion]);

  useEffect(() => {
    if (isRevising) return;
    setIsOngoing(!Boolean(inBetweenStage));
  }, [inBetweenStage, isRevising]);

  useEffect(() => {
    if (!isRevising && next?.nextAssertion && !isLoading) {
      setAssertion(next.nextAssertion);
    }
  }, [next, isRevising, isLoading]);

  const { assertionsBgImgs } = useAssertionBgImgs();

  const availableImages = useMemo(
    () => (assertionsBgImgs || []).filter((i) => Boolean(i?.image)) as unknown as Assertions.AssertionImage[],
    [assertionsBgImgs],
  );

  const anticipatedOffset = useMemo(() => {
    if (isRevising) return 0;
    if (!assertion || !next?.nextAssertion) return 0;
    return assertion.id !== next.nextAssertion.id ? 1 : 0;
  }, [isRevising, assertion, next?.nextAssertion]);

  const assertionImage = useMemo<Assertions.AssertionImage>(() => {
    if (!availableImages.length) {
      return { image: baseimage } as unknown as Assertions.AssertionImage;
    }
    const answeredSoFar = (next?.totalAnswered ?? 0) + anticipatedOffset;
    const index = answeredSoFar % availableImages.length;
    return availableImages[index];
  }, [availableImages, next?.totalAnswered, anticipatedOffset]);

  const assertionKey = useMemo(() => {
    if (!assertion) return 'no-assertion';
    return `${assertion.id}-${Math.random()}`;
  }, [assertion]);

  const showSwipeHint = useMemo(
    () => !isBackLoading && !isRevising && (next?.totalAnswered ?? 0) === 0,
    [isBackLoading, isRevising, next?.totalAnswered],
  );

  if (status !== 'success' || !next) {
    return null;
  }

  return (
    <Box height={`calc(100dvh - ${headerAndFooterHeight}px - env(safe-area-inset-top) - env(safe-area-inset-bottom))`}>
      <PermissionedView permission="assertions.list">
        {inBetweenStage && !isOngoing && (
          <Box height="100%" p={2} sx={{ overflowY: 'auto' }}>
            <InBetweenStage type={inBetweenStage} onContinue={onContinue} isCalculating={isCalculatingMatches} />
          </Box>
        )}

        {isOngoing && (
          <Stack height="100%" p={3}>
            {isBackLoading ? (
              <Box sx={{ height: '100%' }}>
                <AssertionCardLoading
                  isFront
                  disableCountdown
                  message="Loading previous statement..."
                />
              </Box>
            ) : (
              !!assertion && (
                <SwipableCardStack key={assertionKey} onFinish={noop}>
                  {isLoading ? (
                    <FunFactLoading assertionKey={assertionKey} />
                  ): (
                    <Box component="span">
                      <SwipableCard
                        id={assertion.id}
                        onSwipeLeft={() => onAnswer('NO')}
                        onSwipeRight={() => onAnswer('YES')}
                        onSwipeDown={() => onAnswer('NOT_SURE')}
                        onSwipeUp={() => onAnswer('ABSOLUTELY')}
                        rightLabel="Agree"
                        rightLabelStyles={{ fontSize: isDesktop ? 120 : 42 }}
                        leftLabel="Disagree"
                        leftLabelStyles={{ fontSize: isDesktop ? 120 : 42 }}
                        upLabel="Strongly Agree"
                        upLabelStyles={{ fontSize: isDesktop ? 120 : 42 }}
                        downLabel="Neutral"
                        downLabelStyles={{ fontSize: isDesktop ? 120 : 48 }}
                        shouldShowHint={showSwipeHint}
                      >
                        <AssertionCard assertion={assertion} assertionImage={assertionImage} />
                      </SwipableCard>
                    </Box>
                  )}
                </SwipableCardStack>
              )
            )}
            <Authenticated>
              <Suspense>
                <Box pt={4} pb={3} px={2}>
                  <Stack direction="column">
                    <Stack direction="row" justifyContent="center" mb={3} spacing={2}>
                      <Button
                        onClick={() => onAnswer('NO')}
                        size="large"
                        color="error"
                        disabled={isLoading}
                        startIcon={<Close/>}
                        sx={{
                          bgcolor: 'inherit',
                          minWidth: isDesktop ? 134 : undefined,
                          display: isDesktop ? undefined : 'none',
                          border: '2px solid',
                          '&.Mui-disabled': {
                            opacity: 0.4,
                            borderColor: 'divider',
                          },
                        }}
                      >
                        Disagree
                      </Button>
                      <Button
                        onClick={() => onAnswer('NOT_SURE')}
                        size="large"
                        disabled={isLoading}
                        color="inherit"
                        startIcon={<Remove/>}
                        sx={{
                          bgcolor: 'inherit',
                          display: isDesktop ? undefined : 'none',
                          minWidth: isDesktop ? 134 : undefined,
                          border: '2px solid',
                          '&.Mui-disabled': {
                            opacity: 0.4,
                            borderColor: 'divider',
                          },
                        }}
                      >
                        Neutral
                      </Button>
                      {isDesktop ? (
                        <Button
                          onClick={onBack}
                          size={isDesktop ? 'large' : 'medium'}
                          color="primary"
                          startIcon={(
                            <RotateLeftRoundedIcon
                              sx={{
                                '@keyframes spin': {
                                  from: { transform: 'rotate(0deg)' },
                                  to: { transform: 'rotate(-360deg)' },
                                },
                                animation: isBackLoading ? 'spin 0.9s linear infinite' : 'none',
                                transformOrigin: 'center',
                                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                              }}
                            />
                          )}
                          disabled={isRevising || isLoading || isAtStageStart}
                          sx={{
                            bgcolor: 'inherit',
                            minWidth: isDesktop ? 134 : undefined,
                            border: '2px solid',
                            '&.Mui-disabled': {
                              opacity: 0.4,
                              borderColor: 'divider',
                            },
                          }}
                        >
                          Go Back
                        </Button>
                      ) : (
                        <Tooltip title="Go Back">
                          <IconButton
                            onClick={onBack}
                            size={isDesktop ? 'large' : 'medium'}
                            color="primary"
                            disabled={isRevising || isLoading || isAtStageStart}
                            sx={{ bgcolor: 'inherit',
                              border: '2px solid',
                              '&.Mui-disabled': {
                                opacity: 0.4,
                                borderColor: 'divider',
                              },
                            }}
                          >
                            <RotateLeftRoundedIcon
                              sx={{
                                '@keyframes spin': {
                                  from: { transform: 'rotate(0deg)' },
                                  to: { transform: 'rotate(-360deg)' },
                                },
                                animation: isBackLoading ? 'spin 0.9s linear infinite' : 'none',
                                transformOrigin: 'center',
                                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Button
                        onClick={() => onAnswer('YES')}
                        size="large"
                        color="success"
                        disabled={isLoading}
                        startIcon={<Check/>}
                        sx={{
                          bgcolor: 'inherit',
                          display: isDesktop ? undefined : 'none',
                          border: '2px solid',
                          minWidth: isDesktop ? 134 : undefined,
                          '&.Mui-disabled': {
                            opacity: 0.4,
                            borderColor: 'divider',
                          } }}
                      >
                        Agree
                      </Button>
                      <Button
                        onClick={() => onAnswer('ABSOLUTELY')}
                        size="large"
                        color="info"
                        disabled={isLoading}
                        startIcon={<Star/>}
                        sx={{
                          bgcolor: 'inherit',
                          display: isDesktop ? undefined : 'none',
                          border: '2px solid',
                          '&.Mui-disabled': {
                            opacity: 0.4,
                            borderColor: 'divider',
                          } }}
                      >
                        Strongly Agree
                      </Button>
                    </Stack>
                    <UserProgress
                      slotProps={{
                        stack: {
                          direction: 'column',
                          spacing: 0.5,
                        },
                        linearProgress: {
                          sx: {
                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                            height: 10,
                          },
                        },
                        typography: {
                          longFormat: true,
                          fontWeight: 500,
                          color: 'secondary.dark',
                        },
                      }}
                    />
                  </Stack>
                </Box>
              </Suspense>
            </Authenticated>
          </Stack>
        )}
      </PermissionedView>
    </Box>
  );
};

export const InBetweenStage: React.FC<{
  type: Assertions.AnswerStage | 'end';
  onContinue: () => void;
  isCalculating: boolean;
}> = ({ type, onContinue, isCalculating = false }) => {
  const { width, height } = useWindowSize();
  const { user } = useMe();

  const isLoadingMatches = isCalculating;
  const [showConfetti, setShowConfetti] = useState(type !== 'disciplines' && !isLoadingMatches);

  useEffect(() => {
    setShowConfetti(!isLoadingMatches && type !== 'disciplines');
  }, [isLoadingMatches, type]);

  const title = useMemo(() => {
    if (isLoadingMatches) {
      return 'Calculating your matches...';
    }
    switch (type) {
    case 'disciplines':
      return 'Shape Your Future Beyond The Degree';
    case 'majors':
      return <>You Matched <br /> These Disciplines</>;
    case 'universities':
      return <>You Matched <br /> These Majors</>;
    case 'end':
      return <>You Matched <br /> These Universites</>;
    default:
      return null;
    }
  }, [type, isLoadingMatches]);

  const subtitle = useMemo(() => {
    if (isLoadingMatches) return 'Hang tight - this will take a few moments';
    switch (type) {
    case 'disciplines':
      return <>Start answering to reveal academic fields.<br/>We'll match you to the best opportunities,<br/>step by step.</>;
    case 'majors':
      return 'According to your answers, you should follow an academic path within these disciplines.';
    case 'end':
      return 'According to your answers, you should follow an academic path at these universites. Review your matches, explore your best-fit majors, and apply to the one that’s right for you.';
    default:
      return null;
    }
  }, [type, isLoadingMatches]);

  const button = useMemo(() => {
    if (isLoadingMatches) return null;
    switch (type) {
    case 'disciplines':
      return 'Start';
    case 'majors':
    case 'universities':
      return 'Continue';
    case 'end':
      return 'View Matches';
    default:
      return null;
    }
  }, [type, isLoadingMatches]);

  const list = useMemo(() => {
    if (isLoadingMatches) return null;
    switch (type) {
    case 'majors':
      return (
        <PermissionedView permission="disciplines.list">
          <Paper variant="elevation" elevation={1} sx={{ position: 'relative' }}>
            <Suspense fallback={(
              <ListLoading
                disablePadding
                repeat={3}
                renderItem={() => <DisciplineRowSkeleton />}
              />
            )}>
              <DisciplinesList
                filters={{
                  limit: 3,
                }}
                renderItem={discipline => <DisciplineRow key={discipline.id} {...discipline} />}
                options={{
                  enablePagination: false,
                }}
                slots={{
                  after: ({ count }) => (
                    <Box mt={2} textAlign="center">
                      <Typography variant="body2" color="secondary.dark" fontWeight={600}>
                          +{Math.max((count ?? 0) - 3, 0)} others
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Suspense>
          </Paper>
        </PermissionedView>
      );
    case 'universities':
      return (
        <PermissionedView permission="majors.list">
          <Paper variant="elevation" elevation={1} sx={{ position: 'relative' }}>
            <Suspense fallback={(
              <ListLoading
                disablePadding
                repeat={3}
                renderItem={() => <MajorRowSkeleton/>}
              />
            )}>
              <MajorsList
                filters={{
                  limit: 3,
                }}
                renderItem={major => <MajorRow key={major.id} {...major} />}
                options={{
                  enablePagination: false,
                }}
                slots={{
                  after: ({ count }) => (
                    <Box mt={2} textAlign="center">
                      <Typography variant="body2" color="secondary.dark" fontWeight={600}>
                          +{Math.max((count ?? 0) - 3, 0)} others
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Suspense>
          </Paper>
        </PermissionedView>
      );
    case 'end':
      return (
        <PermissionedView permission="universities.list">
          <Paper variant="elevation" elevation={1} sx={{ position: 'relative' }}>
            <Suspense fallback={(
              <ListLoading
                disablePadding
                repeat={3}
                renderItem={() => <UniversityRowSkeleton/>}
              />
            )}>
              <UniversitiesList
                filters={{
                  limit: 3,
                }}
                renderItem={university => <UniversityRow key={university.id} {...university} />}
                options={{
                  enablePagination: false,
                }}
                slots={{
                  after: ({ count }) => (
                    <Box mt={2} textAlign="center">
                      <Typography variant="body2" color="secondary.dark" fontWeight={600}>
                          +{Math.max((count ?? 0) - 3, 0)} others
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Suspense>
          </Paper>
        </PermissionedView>
      );
    default:
      return null;
    }
  }, [type, isLoadingMatches]);

  return (
    <Fragment>
      {showConfetti && (
        <Box position="absolute" top={0} right={0} bottom={0} left={0}>
          <ReactConfetti
            width={width}
            height={height}
            recycle={false}
            onConfettiComplete={() => setShowConfetti(false)}
          />
        </Box>
      )}
      <Stack minHeight="100%" direction="column" spacing={3} pb={3}>
        <Stack direction="column" justifyContent="space-between" spacing={2} sx={{ flex: 1 }}>
          <Box p={1} borderRadius={3} textAlign="center">
            <Typography
              variant="h1"
              fontSize={28}
              fontWeight={900}
              fontFamily="'Rubik', sans-serif"
              sx={{
                background: '#FFF none repeat scroll 0 0',
                boxDecorationBreak: 'clone',
                display: 'inline',
                px: 2,
                py: 1,
                lineHeight: 2,
                boxShadow: '-10px 10px 0 #000',
                border: '2px solid #000',
                borderRadius: 1,
              }}
            >
              {title}
            </Typography>
          </Box>
          {type === 'disciplines' && !isLoadingMatches ? (
            <Box px={2} width="100%" display="flex" justifyContent="center" alignItems="center">
              <StartPageSwipableInfomration />
            </Box>
          ) : isLoadingMatches ? (
            <Box px={2} width="100%" display="flex" justifyContent="center" alignItems="center">
              <Stack alignItems="center" spacing={2}>
                <Typography variant="body2" color="secondary.dark" textAlign="center">
                  {subtitle}<AnimatedDots/>
                </Typography>
              </Stack>
            </Box>
          ) : (
            <>
              {subtitle && (
                <Typography variant="body2" color="secondary.dark" textAlign="center" px={2}>
                  {subtitle}
                </Typography>
              )}
              {type === 'universities' && user?.archetype && (
                <Suspense>
                  <AcademicArchetype
                    archetype={user.archetype}
                    archetypeDescription={user.archetypeDescription}
                    slots={{
                      share: (
                        <ArchetypeShareButton
                          archetype={user.archetype}
                          archetypeDescription={user.archetypeDescription}
                        />
                      ),
                    }}
                  />
                </Suspense>
              )}
              {list}
            </>
          )}
        </Stack>

        {button && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onContinue}
          >
            {button}
          </Button>
        )}
      </Stack>
    </Fragment>
  );
};

